import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Download, Loader2, Upload, Trash2, Copy, FileText, Globe, Lock, Pencil, AlertTriangle,
} from 'lucide-react';
import ConfirmDialog from './admin/ConfirmDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtSize = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * DownloadManagerPanel — OL UI replacement for the WP Download Manager plugin.
 * Uploads live in OL (GridFS master) + mirror to the WP media library. Each
 * document has a "Show on website" toggle; when OFF, its shortcode renders
 * nothing on WordPress. The WP theme uses [ol_download id="..."] / [ol_downloads].
 */
export default function DownloadManagerPanel({ orgId }) {
  const { isDark } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/downloads/${orgId}`, { withCredentials: true });
      setItems(data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load downloads');
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const toggleWp = async (doc) => {
    const next = !doc.show_on_wp;
    setTogglingId(doc.id);
    setItems((prev) => prev.map((d) => (d.id === doc.id ? { ...d, show_on_wp: next } : d)));
    try {
      await axios.patch(`${API}/api/downloads/${orgId}/${doc.id}`,
        { show_on_wp: next }, { withCredentials: true });
      toast.success(next ? 'Shown on website' : 'Hidden from website');
    } catch (e) {
      setItems((prev) => prev.map((d) => (d.id === doc.id ? { ...d, show_on_wp: doc.show_on_wp } : d)));
      toast.error(e.response?.data?.detail || 'Toggle failed');
    } finally { setTogglingId(null); }
  };

  const copyShortcode = (doc) => {
    navigator.clipboard?.writeText(doc.shortcode);
    toast.success(`Copied ${doc.shortcode}`);
  };

  const handleDelete = (doc) => setConfirmState({
    title: 'Delete this document?',
    description: `"${doc.title}" will be removed from OmniLeagues and WordPress. Any download link on the site will disappear. This cannot be undone.`,
    confirmLabel: 'Yes, delete',
    destructive: true,
    onConfirm: async () => {
      try {
        await axios.delete(`${API}/api/downloads/${orgId}/${doc.id}`, { withCredentials: true });
        toast.success('Document deleted');
        await load();
      } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    },
  });

  return (
    <div className="space-y-4" data-testid="download-manager-panel">
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <Card className={card}>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-500/15"><Download className="h-5 w-5 text-teal-400" /></div>
              <div>
                <CardTitle>Download Manager</CardTitle>
                <p className={`text-sm ${muted} mt-0.5`}>
                  Upload documents once — stored in OmniLeagues and mirrored to WordPress.
                  Copy a document's shortcode onto any WP page. Toggle "Show on website" off
                  to hide its download link without deleting the file.
                </p>
              </div>
            </div>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700"
              onClick={() => setShowUpload(true)} data-testid="dm-upload-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Add Document
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-slate-700 rounded-lg">
              <FileText className="h-8 w-8 mx-auto mb-2 text-slate-600" />
              <p className={muted}>No documents yet. Click "Add Document" to upload your first download.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="dm-list">
              {items.map((d) => (
                <div key={d.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    isDark ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                  data-testid={`dm-row-${d.id}`}>
                  <FileText className="h-5 w-5 text-teal-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{d.title}</span>
                      {d.category && <Badge variant="outline" className="text-[10px]">{d.category}</Badge>}
                      <Badge className={d.show_on_wp
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]'
                        : 'bg-slate-600/30 text-slate-400 border-slate-600 text-[10px]'}>
                        {d.show_on_wp ? <Globe className="h-2.5 w-2.5 mr-1" /> : <Lock className="h-2.5 w-2.5 mr-1" />}
                        {d.show_on_wp ? 'On website' : 'Hidden'}
                      </Badge>
                    </div>
                    <div className={`text-[11px] ${muted} truncate`}>
                      {d.file_name} · {fmtSize(d.size_bytes)} · {d.download_count || 0} downloads
                    </div>
                    <button
                      onClick={() => copyShortcode(d)}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-teal-400 hover:text-teal-300"
                      data-testid={`dm-shortcode-${d.id}`}>
                      <Copy className="h-3 w-3" /> {d.shortcode}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1.5">
                      {togglingId === d.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-teal-500" />}
                      <Switch checked={!!d.show_on_wp} onCheckedChange={() => toggleWp(d)}
                        data-testid={`dm-toggle-${d.id}`} />
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8"
                      onClick={() => setEditDoc(d)} data-testid={`dm-edit-${d.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:text-red-300"
                      onClick={() => handleDelete(d)} data-testid={`dm-delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className={`text-[11px] ${muted} mt-3`}>
            Full list shortcode: <code className="text-teal-400 font-mono">[ol_downloads]</code> renders
            every visible document as a download list.
          </p>
        </CardContent>
      </Card>

      <UploadDialog open={showUpload} orgId={orgId}
        onClose={() => setShowUpload(false)} onDone={load} />
      <EditDialog doc={editDoc} orgId={orgId}
        onClose={() => setEditDoc(null)} onDone={load} />
    </div>
  );
}

function UploadDialog({ open, orgId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [showOnWp, setShowOnWp] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => { setFile(null); setTitle(''); setDescription(''); setCategory(''); setShowOnWp(true); setErr(null); setBusy(false); };

  const submit = async () => {
    if (!file || !title.trim()) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      fd.append('description', description);
      fd.append('category', category);
      fd.append('show_on_wp', showOnWp ? 'true' : 'false');
      const { data } = await axios.post(`${API}/api/downloads/${orgId}`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.ok) { toast.success('Document uploaded'); onDone?.(); reset(); onClose?.(); }
      else setErr(data.error?.message || 'Upload failed');
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) { reset(); onClose?.(); } }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg" data-testid="dm-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4 text-teal-400" /> Add Document</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            The file is stored in OmniLeagues and mirrored to WordPress. You'll get a shortcode to place on any page.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Title <span className="text-red-400">*</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Sponsorship Packages 2026"
              className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-sm" data-testid="dm-title" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
              className="bg-slate-800 border-slate-700 text-slate-100 text-sm min-h-[60px]" data-testid="dm-desc" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Category (optional)</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. sponsorship, forms"
              className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-sm" data-testid="dm-category" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">File (max 25 MB)</Label>
            <Input type="file" accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs file:mr-2 file:bg-slate-700 file:text-slate-200 file:border-0 file:rounded file:px-2 file:py-1"
              data-testid="dm-file" />
            {file && <p className="text-[10px] text-slate-400">{file.name} — {fmtSize(file.size)}</p>}
          </div>
          <div className="flex items-center justify-between p-2 rounded-md bg-slate-800/50 border border-slate-700">
            <span className="text-xs">Show on website immediately</span>
            <Switch checked={showOnWp} onCheckedChange={setShowOnWp} data-testid="dm-show-toggle" />
          </div>
          {err && <div className="p-2 rounded-md bg-red-900/20 border border-red-700/40 text-red-300 text-xs flex items-start gap-1.5">
            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => { reset(); onClose?.(); }}
            className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700">Cancel</Button>
          <Button disabled={busy || !file || !title.trim()} onClick={submit}
            className="bg-teal-600 hover:bg-teal-700" data-testid="dm-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ doc, orgId, onClose, onDone }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (doc) { setTitle(doc.title || ''); setDescription(doc.description || ''); setCategory(doc.category || ''); }
  }, [doc]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/api/downloads/${orgId}/${doc.id}`,
        { title, description, category }, { withCredentials: true });
      toast.success('Document updated'); onDone?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg" data-testid="dm-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4 text-teal-400" /> Edit Document</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-sm" data-testid="dm-edit-title" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
              className="bg-slate-800 border-slate-700 text-slate-100 text-sm min-h-[60px]" data-testid="dm-edit-desc" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)}
              className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-sm" data-testid="dm-edit-category" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onClose?.()}
            className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700">Cancel</Button>
          <Button disabled={busy} onClick={submit} className="bg-teal-600 hover:bg-teal-700" data-testid="dm-edit-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
